import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { Modal, ModalContent } from '@awazevr/design-system.components.modal'
import { Drawer, DrawerContent } from '@awazevr/design-system.components.drawer'
import {
  MEDIA_QUERY_BREAKPOINTS,
  useWindowWidth,
} from '@awazevr/design-system.utils.react'

type ResponsiveModalProps = {
  isOpen: boolean
  children: ReactNode
  /** Called when the DS asks to close; unused while `disableClose` is set. */
  close?: () => void
  /**
   * Hide the close control and ignore Escape and overlay clicks. The children
   * must then contain a focusable element, or the DS focus trap throws.
   */
  disableClose?: boolean
}

const MOBILE_PADDING_PX = 16
const DESKTOP_PADDING_PX = 24
const DRAWER_BOTTOM_PADDING_PX = 40

const ModalPadding = styled.div`
  box-sizing: border-box;
  padding: ${DESKTOP_PADDING_PX}px;
`

const DrawerPadding = styled.div`
  box-sizing: border-box;
  padding: ${MOBILE_PADDING_PX}px ${MOBILE_PADDING_PX}px
    ${DRAWER_BOTTOM_PADDING_PX}px;
`

const noop = () => undefined

// A bottom drawer below the mobile breakpoint, a modal above it, both without
// the DS header (`hasHeader={false}`) so callers draw their own. Reusable by
// the Phase 2 drawer flow.
export const ResponsiveModal = ({
  isOpen,
  children,
  close = noop,
  disableClose = false,
}: ResponsiveModalProps): React.ReactElement | null => {
  const width = useWindowWidth()

  // The breakpoint is only known on the client; the DS portals render there too.
  if (width === undefined) {
    return null
  }

  if (width < MEDIA_QUERY_BREAKPOINTS.mobile) {
    return (
      <Drawer isOpen={isOpen} close={close} placement="bottom-content-fit">
        <DrawerContent
          hasPadding={false}
          hasHeader={false}
          disableClose={disableClose}
        >
          <DrawerPadding data-testid="responsive-drawer">
            {children}
          </DrawerPadding>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Modal isOpen={isOpen} close={close} disableClose={disableClose}>
      <ModalContent hasPadding={false} hasHeader={false}>
        <ModalPadding data-testid="responsive-modal">{children}</ModalPadding>
      </ModalContent>
    </Modal>
  )
}
